import { LoginValidationDto } from "@/interfaces/LoginValidationDto";
import * as UrlConsts from "@/scripts/UrlConsts";

export class AuthenticationService {
  static readonly TOKEN_REFRESH_AGE_MS = 60 * 60 * 1000; // 1 heure
  private static refreshInProgress = false;

  /**
   * @desc initialise la session d'un utilisateur
   * @param loginValidationDto données de session
   */
  static setSession(loginValidationDto: LoginValidationDto): void {
    localStorage.setItem("accessToken", loginValidationDto.token);
    localStorage.setItem("expiresAt", loginValidationDto.expiresAt.toString());
    localStorage.setItem("username", loginValidationDto.username);
    localStorage.setItem("userStatus", loginValidationDto.status.toString());
    localStorage.setItem("tokenIssuedAt", new Date().getTime().toString());
  }

  /**
   * supprime les données de session stockées en front
   */
  static async logout(axios: any): Promise<void> {
    const currentToken = this.getToken();
    this.removeSession();

    if (!currentToken) {
      return;
    }

    try {
      const headers = {
        headers: { Authorization: `Bearer ${currentToken}` },
      };
      axios
        .post(UrlConsts.logout, { token: currentToken }, headers)
        .catch((error: any) => {
          console.error(error);
        });
    } catch (error) {
      console.error(error);
    }
  }

  private static removeSession(): void {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("expiresAt");
    localStorage.removeItem("username");
    localStorage.removeItem("userStatus");
    localStorage.removeItem("tokenIssuedAt");
  }

  /**
   * @desc vérifie si l'utilisateur est connecté
   */
  static isLoggedIn(): boolean {
    if (!this.getToken()) return false;
    return !this.isTokenExpired();
  }

  /**
   * @desc vérifie si l'utilisateur est déconnecté
   */
  static isLoggedOut(): boolean {
    return !this.isLoggedIn();
  }

  /**
   * @desc vérifie si le token est expiré
   */
  static isTokenExpired(): boolean {
    const expiresAt = localStorage.getItem("expiresAt");
    if (!expiresAt) return false;

    return new Date(expiresAt).getTime() - Date.now() < 0;
  }

  /**
   * @desc vérifie si le token a été émis il y a plus que la durée donnée
   */
  static isTokenOlderThan(durationMs: number): boolean {
    const issuedAt = this.getTokenIssuedAt();
    if (issuedAt == null) return true;

    return Date.now() - issuedAt > durationMs;
  }

  /**
   * @desc raffraichit le token si l'utilisateur est connecté et que le token a plus d'une heure
   */
  static async refreshTokenIfNeeded(axios: any): Promise<number> {
    if (this.refreshInProgress) return 0;
    if (!this.getToken() || this.isTokenExpired()) return 0;
    if (!this.isTokenOlderThan(this.TOKEN_REFRESH_AGE_MS)) return 0;

    return this.checkTokenValidation(axios);
  }

  private static getTokenIssuedAt(): number | null {
    const stored = localStorage.getItem("tokenIssuedAt");
    if (stored) {
      const timestamp = Number(stored);
      if (!Number.isNaN(timestamp)) return timestamp;
    }
    return null;
  }

  /**
   * @desc renvoie le token stocké en front
   */
  static getToken(): string {
    return localStorage.getItem("accessToken") as any;
  }

  /**
   * @desc renvoie l'header contenant le token de connexion
   * @returns
   */
  static getRequestHeader(): any {
    const token = AuthenticationService.getToken();
    return { headers: { Authorization: `Bearer ${token}` } };
  }

  /**
   * @desc renvoie le nom de l'utilisateur connecté
   * @returns
   */
  static getUsername(): string {
    return localStorage.getItem("username") as any;
  }

  /**
   * @desc renvoie le statut de l'utilisateur connecté
   * @returns
   */
  static getUserStatus(): number {
    return +(localStorage.getItem("userStatus") ?? 0);
  }

  static async checkTokenValidation(axios: any): Promise<number> {
    const currentToken = this.getToken();
    if (!currentToken) return 0;
    if (this.refreshInProgress) return 0;

    this.refreshInProgress = true;

    return new Promise((resolve) => {
      const headers = AuthenticationService.getRequestHeader();

      axios
        .post(UrlConsts.refreshToken, { token: currentToken }, headers)
        .then((response: any) => {
          if (!this.getToken()) {
            resolve(0);
            return;
          }
          AuthenticationService.setSession(response.data);
          resolve(1);
        })
        .catch((error: any) => {
          if (error.response && error.response.status === 401) {
            this.logout(axios);
            resolve(2);
          }
          resolve(0);
        })
        .finally(() => {
          this.refreshInProgress = false;
        });
    });
  }
}
